/**
 * G2.27 — Per-table encryption configuration.
 *
 * For each Dexie table, lists the fields that MUST stay plaintext so Dexie
 * indexes continue to work. Everything else is bundled into a single
 * `_enc` column (an `EncryptedPayload`) at write time and merged back
 * at read time by the encryption middleware.
 *
 * Design decisions:
 *
 *   1. Primary keys + every indexed field stay plaintext — Dexie cannot
 *      index over encrypted bytes and AES-GCM is non-deterministic (no
 *      equality matches across ciphertexts of the same plaintext).
 *
 *   2. `customers.phone` and `customers.email` stay plaintext indexed.
 *      This is an explicit tradeoff: those indexes back every CRM
 *      lookup at the till. A stolen device without the KEK can still
 *      read phone/email but NOT loyalty balance, notes, or any bill
 *      linkage. If that's unacceptable, a future PR can switch to
 *      HMAC-indexed lookups (deterministic tags over a device-scoped
 *      HMAC key). For now, phone+email plaintext is the pragmatic
 *      line.
 *
 *   3. `sync_metadata` is intentionally not encrypted — it contains
 *      only ISO timestamps keyed by entity type. Neither the key nor
 *      the value leaks tenant data.
 *
 *   4. Tables listed here MUST include every currently-indexed field.
 *      Forgetting one silently breaks queries (the index sees `undefined`
 *      after decryption). See `pos-database.test.ts` for a round-trip
 *      assertion per table.
 */

export const TABLES_WITH_ENCRYPTION = [
  'pending_sync',
  'products',
  'customers',
  'draft_bills',
  'local_quotes',
  'credit_snapshots',
] as const;

export type EncryptedTableName = (typeof TABLES_WITH_ENCRYPTION)[number];

/**
 * Fields kept plaintext per table. Anything not in this list is moved
 * into the encrypted `_enc` payload on write and merged back on read.
 */
export const PLAINTEXT_FIELDS: Record<EncryptedTableName, readonly string[]> = {
  pending_sync: [
    'id',
    'client_id',
    'entity_type',
    'operation',
    'device_id',
    'status',
    'created_at',
    'retry_count',
    // `last_error` is kept plaintext because the sync-queue `update()`
    // path writes it at the top level without going through the split
    // (Dexie update patches object properties in place). The field is a
    // short diagnostic message, not PII, so leaving it plaintext is a
    // deliberate tradeoff for simpler code at the update site.
    'last_error',
    // T19: written by the same top-level update() when a bill syncs. A server
    // id / bill number and a timestamp — not PII.
    'server_id',
    'server_bill_number',
    'synced_at',
  ],
  products: [
    // Pragmatic line on products: the name/SKU/price/stock/unit/HSN of a
    // product catalogue are already visible on shelves, receipts, and
    // the UI, so encrypting them adds friction without meaningful
    // confidentiality gain (the app filters/searches by name every
    // keystroke — running every search through AES-GCM is a real UX
    // cost). The *only* truly sensitive field is `cost`, which leaks
    // margins to a competitor with the device. Everything else stays
    // plaintext so existing indexed queries keep working untouched.
    'id',
    'tenant_id',
    'location_id',
    'name',
    'sku',
    'barcode',
    'category',
    'price',
    'gst_rate',
    'hsn_code',
    'stock_quantity',
    'unit',
    'is_active',
    'synced_at',
    // C12: variant shape. `parent_product_id` is indexed (a parent's children);
    // the rest is catalogue structure, not sensitive.
    'parent_product_id',
    'has_variants',
    'is_variant',
    'variant_attributes',
  ],
  customers: [
    // `phone` and `email` stay indexed plaintext for CRM lookup at the till
    // (see file header for the tradeoff). `name` stays plaintext because
    // the customer-lookup UI searches by name prefix via Dexie's
    // `startsWithIgnoreCase`, which can't see through AES-GCM. `loyalty_points`
    // is the single encrypted field — it's the only sensitive business
    // signal on this row (a stolen device can reveal redemption history
    // across the tenant's full customer base).
    'id',
    'name',
    'phone',
    'email',
    'synced_at',
  ],
  draft_bills: [
    'client_id',
    'location_id',
    'is_parked',
    'parked_reference',
    'is_inter_state',
    'created_at',
    'updated_at',
  ],
  local_quotes: [
    'client_id',
    'server_id',
    'tenant_id',
    'location_id',
    'status',
    'is_synced',
    'created_at',
    'updated_at',
  ],
  // L02: what a credit customer owes is a business signal like loyalty points;
  // only the key and the time it was read stay plaintext.
  credit_snapshots: [
    'customer_id',
    'as_of',
  ],
};

/** The reserved column where we park the encrypted blob. */
export const ENC_COLUMN = '_enc';

export function isEncryptedTable(name: string): name is EncryptedTableName {
  return (TABLES_WITH_ENCRYPTION as readonly string[]).includes(name);
}
